import { useMemo } from 'react';
import { View, Text, ScrollView, Pressable, Image } from 'react-native';
import { Trophy } from 'lucide-react-native';
import { useTheme } from '../../../src/theme/useTheme';
import { ARENA_EMPTY_MONKEY_SOURCE } from '../../../constants/assets';
import type { CompetitionSummary } from '../../../src/competitions/types/competitions.types';
import {
  groupCompetitionsIntoSections,
  pickHeroCompetition,
} from '../../../src/competitions/helpers/competitionSections.helper';
import { CompetitionCard } from './CompetitionCard';
import { CompetitionHeroCard } from './CompetitionHeroCard';
import {
  HUB_TITLE,
  HUB_SECTION_TITLES,
  HUB_EMPTY_TITLE,
  HUB_EMPTY_SUBTITLE,
  HUB_ERROR_TITLE,
  HUB_ERROR_RETRY,
  HUB_TEST_IDS,
} from './CompetitionsHub.constants';
import { getCompetitionsHubStyles } from './CompetitionsHub.styles';

export interface CompetitionsHubViewProps {
  competitions: CompetitionSummary[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  /** Returns a press handler for a competition, or null when it has no destination yet. */
  resolveOnPress: (competition: CompetitionSummary) => (() => void) | null;
}

export function CompetitionsHubView({
  competitions, isLoading, isError, onRetry, resolveOnPress,
}: CompetitionsHubViewProps) {
  const theme = useTheme();
  const s = useMemo(() => getCompetitionsHubStyles(theme), [theme]);

  const header = (
    <View style={s.headerRow}>
      <Trophy size={22} color={theme.accent} />
      <Text style={s.headerTitle}>{HUB_TITLE}</Text>
    </View>
  );

  if (isLoading) {
    return (
      <View style={s.screen} testID={HUB_TEST_IDS.skeleton}>
        {header}
        <View style={s.scrollContent}>
          <View style={s.skeletonCard} />
          <View style={s.skeletonCard} />
          <View style={s.skeletonCard} />
        </View>
      </View>
    );
  }

  if (isError) {
    return (
      <View style={s.screen} testID={HUB_TEST_IDS.screen}>
        {header}
        <View style={s.centerWrap}>
          <Image source={ARENA_EMPTY_MONKEY_SOURCE} style={s.emptyMonkey} resizeMode="contain" />
          <Text style={s.stateTitle}>{HUB_ERROR_TITLE}</Text>
          <Pressable style={s.retryButton} onPress={onRetry} testID={HUB_TEST_IDS.retry} accessibilityRole="button">
            <Text style={s.retryLabel}>{HUB_ERROR_RETRY}</Text>
          </Pressable>
        </View>
      </View>
    );
  }

  if (competitions.length === 0) {
    return (
      <View style={s.screen} testID={HUB_TEST_IDS.screen}>
        {header}
        <View style={s.centerWrap}>
          <Image source={ARENA_EMPTY_MONKEY_SOURCE} style={s.emptyMonkey} resizeMode="contain" />
          <Text style={s.stateTitle}>{HUB_EMPTY_TITLE}</Text>
          <Text style={s.stateSubtitle}>{HUB_EMPTY_SUBTITLE}</Text>
        </View>
      </View>
    );
  }

  // Future-first: the next thing to play gets the hero slot; its section card is
  // omitted so it never appears twice. Sections that end up empty disappear.
  const hero = pickHeroCompetition(competitions);
  const sections = groupCompetitionsIntoSections(competitions)
    .map((section) => ({
      ...section,
      competitions: section.competitions.filter((c) => c.id !== hero?.id),
    }))
    .filter((section) => section.competitions.length > 0);

  return (
    <View style={s.screen} testID={HUB_TEST_IDS.screen}>
      {header}
      <ScrollView contentContainerStyle={s.scrollContent} showsVerticalScrollIndicator={false}>
        {hero != null && (
          <CompetitionHeroCard competition={hero} onPress={resolveOnPress(hero)} />
        )}
        {sections.map((section) => (
          <View key={section.key} testID={HUB_TEST_IDS.section(section.key)}>
            <Text style={s.sectionHeader}>{HUB_SECTION_TITLES[section.key]}</Text>
            {section.competitions.map((competition) => (
              <CompetitionCard
                key={competition.id}
                competition={competition}
                onPress={resolveOnPress(competition)}
                compact={section.key === 'past'}
              />
            ))}
          </View>
        ))}
      </ScrollView>
    </View>
  );
}
